import React, { useState } from "react";
import { toast } from "sonner";
import { ChatCircleDots, IdentificationBadge, Copy, Check, User, Ruler, Scales, BatteryCharging, Truck, ArrowCounterClockwise } from "@phosphor-icons/react";
import { API } from "@/lib/api";
import { ASSETS } from "@/lib/assets";

const specValue = (c, re) => c.model_specs?.key_specs?.find((k) => re.test(k.label));

const STATS = [
  { id: "length", label: "Overall Length", icon: Ruler, tab: "specs", get: (c) => specValue(c, /overall length/i) },
  { id: "height", label: "Travel Height", icon: Ruler, tab: "specs", get: (c) => specValue(c, /travel height/i) },
  { id: "atm", label: "ATM", icon: Scales, tab: "specs", get: (c) => specValue(c, /^atm/i) },
  { id: "tare", label: "Tare", icon: Scales, tab: "specs", get: (c) => specValue(c, /^tare/i) },
  { id: "battery", label: "Battery", icon: BatteryCharging, tab: "build", get: (c) => c.battery_configuration && { value: c.battery_configuration } },
  { id: "axle", label: "Axle", icon: Truck, tab: "build", get: (c) => c.axle && { value: `${c.axle} axle` } },
];

function KeyStats({ caravan, onJump }) {
  const stats = STATS.map((s) => ({ ...s, spec: s.get(caravan) })).filter((s) => s.spec);
  const my26 = caravan.model_specs?.specs_fallback;
  if (stats.length < 2) return null;
  return (
    <div className="flex flex-wrap gap-px bg-neutral-800/70 border-t border-neutral-800" data-testid="caravan-key-stats">
      {stats.map((s) => (
        <button
          key={s.id}
          type="button"
          onClick={() => onJump(s.tab)}
          data-testid={`caravan-stat-${s.id}`}
          className="group flex-1 basis-[160px] text-left bg-[#0F0F0F] hover:bg-[#141414] px-5 py-4 transition-colors duration-200 min-w-0"
        >
          <div className="flex items-center gap-1.5 text-[0.62rem] font-medium uppercase tracking-[0.14em] text-neutral-500 group-hover:text-neutral-300 transition-colors">
            <s.icon size={13} className="text-[#73bf44]" weight="duotone" /> {s.label}{s.spec.guide ? " *" : ""}{my26 && s.tab === "specs" ? " · MY26" : ""}
          </div>
          <div className="font-heading text-lg font-semibold text-white mt-1 truncate" title={s.spec.value}>{s.spec.value}</div>
        </button>
      ))}
    </div>
  );
}

function ChassisCopy({ chassis }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(chassis);
      setCopied(true);
      toast.success("Chassis number copied");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Could not copy");
    }
  };
  return (
    <button onClick={copy} data-testid="my-caravan-chassis-copy" title="Copy chassis number" className="group inline-flex items-center gap-2 rounded-full bg-black/40 border border-neutral-800 px-3.5 py-1.5 hover:border-[#73bf44]/50 transition-colors">
      <IdentificationBadge size={16} className="text-[#73bf44]" weight="duotone" />
      <span className="font-mono text-sm tracking-widest text-[#73bf44]">{chassis}</span>
      {copied ? <Check size={14} className="text-[#73bf44]" weight="bold" /> : <Copy size={14} className="text-neutral-500 group-hover:text-white transition-colors" />}
    </button>
  );
}

export function CaravanHero({ caravan, onAskNick, onClear, onJump }) {
  const subtitle = [caravan.exterior_colour, caravan.decal_colour && `${caravan.decal_colour} decals`].filter(Boolean).join(" · ") || "On The Move";
  return (
    <section className="bg-[#0F0F0F] border border-neutral-800 rounded-2xl overflow-hidden shadow-2xl" data-testid="caravan-hero">
      <div className="grid md:grid-cols-[minmax(0,380px)_1fr]">
        <div className="group h-52 md:h-auto md:min-h-[240px] bg-gradient-to-b from-neutral-100 to-neutral-300 flex items-center justify-center p-6 overflow-hidden">
          <img
            src={caravan.model_photo_id ? `${API}/model-photos/${caravan.model_photo_id}/file` : ASSETS.caravan}
            onError={(e) => { e.currentTarget.src = ASSETS.caravan; }}
            alt={caravan.model}
            className="h-full max-h-56 w-full object-contain drop-shadow-[0_12px_24px_rgba(0,0,0,0.35)] group-hover:scale-[1.04] transition-transform duration-500"
          />
        </div>
        <div className="p-6 lg:p-8 flex flex-col justify-center gap-4 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <ChassisCopy chassis={caravan.chassis_number} />
            {caravan.customer_name && (
              <span data-testid="spec-field-customer_name" className="inline-flex items-center gap-1.5 rounded-full bg-[#73bf44]/10 border border-[#73bf44]/25 text-[#73bf44] text-xs font-bold uppercase tracking-wide px-3 py-1.5">
                <User size={13} weight="bold" /> {caravan.customer_name}
              </span>
            )}
          </div>
          <div>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white uppercase tracking-tight leading-none" data-testid="my-caravan-model">{caravan.model || "Caravan"}</h2>
            <div className="text-[0.7rem] tracking-[0.2em] text-neutral-500 uppercase mt-2.5">{subtitle}</div>
          </div>
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <button onClick={onAskNick} data-testid="my-caravan-ask-nick" className="inline-flex items-center gap-2.5 bg-[#73bf44] text-[#050505] rounded-full font-heading font-bold text-sm tracking-wide px-6 py-3 hover:bg-[#5fa338] hover:shadow-[0_0_20px_rgba(115,191,68,0.3)] transition-[background-color,box-shadow,transform] active:scale-[0.98]">
              <ChatCircleDots size={18} weight="fill" /> Ask Nick About This Caravan
            </button>
            <button onClick={onClear} data-testid="my-caravan-clear" className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-white transition-colors px-2 py-2">
              <ArrowCounterClockwise size={14} /> Use a different chassis number
            </button>
          </div>
        </div>
      </div>
      <KeyStats caravan={caravan} onJump={onJump} />
    </section>
  );
}
